import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import CourseHeader from "../components/Courses/Shared/CourseHeader";
import ProgramBenefits from "../components/Courses/Shared/ProgramBenefits";
import AnimatedSection from "../components/Courses/Shared/AnimatedSection";
import ProfessionalCourseApplicationForm from "../components/Courses/ProfessionalCourses/ProfessionalCourseApplicationForm";
import ciscoImg from "../assets/cisco.png";
import cmsApi from "../services/cmsApi";
import { getImageUrl } from "../utils/getImageUrl";
import { getCareerPaths } from "../utils/ciscoCareerPaths";

// ─── Parsers ──────────────────────────────────────────────────────────────────

function parseBenefits(json) {
  try { return JSON.parse(json); } catch { return []; }
}

// ─── Explore Career Paths grid ────────────────────────────────────────────────

function CareerPathsGrid({ rawJson }) {
  const paths = getCareerPaths(rawJson);

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-custom-pink leading-tight">Explore Career Paths</h2>
          <div className="w-28 h-1 bg-gold mx-auto rounded-full mt-4" />
          <p className="mt-6 text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Structured, career-focused learning pathways delivered through Cisco Networking Academy at JRIIT.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {paths.map((path) => (
            <Link
              key={path.slug}
              to={`/courses/professional-courses/cisco-networking-academy/${path.slug}`}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-md hover:border-custom-pink/30 transition-all duration-300 flex flex-col"
            >
              <div className="h-40 bg-gradient-to-br from-custom-pink/10 to-gold/10 flex items-center justify-center px-4">
                {path.badge_image ? (
                  <img
                    src={getImageUrl(path.badge_image)}
                    alt={path.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <span className="text-custom-pink font-bold text-xl text-center">{path.name}</span>
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{path.tag}</span>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{path.name}</h3>
                <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-4">{path.heroIntro}</p>
                <span className="mt-auto text-custom-pink font-bold text-sm flex items-center gap-1">
                  Learn More <span aria-hidden="true">&rarr;</span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Quick Navigation ─────────────────────────────────────────────────────────

const TOC_ITEMS = [
  { id: "intro",                 label: "Cisco Networking Academy" },
  { id: "career-paths",         label: "Explore Career Paths" },
  { id: "why-study",            label: "Why Study at JRIIT?" },
  { id: "admission-requirements", label: "Admission Requirements" },
  { id: "application-form",     label: "Apply Now" },
];

const SECTION_IDS = TOC_ITEMS.map((t) => t.id);

function useActiveSection() {
  const [active, setActive] = useState(SECTION_IDS[0]);
  useEffect(() => {
    const update = () => {
      const navbar = document.querySelector("header.sticky");
      const offset = (navbar ? navbar.getBoundingClientRect().height : 100) + 24;
      let current = SECTION_IDS[0];
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= offset) current = id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, []);
  return active;
}

function QuickNavigation({ activeSection }) {
  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const navbar = document.querySelector("header.sticky");
    const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 100;
    const top = el.getBoundingClientRect().top + window.pageYOffset - navbarHeight - 16;
    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  return (
    <nav className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
      <div className="bg-custom-pink px-5 py-4">
        <p className="text-white font-bold text-sm uppercase tracking-widest">Quick Navigation</p>
      </div>
      <ol className="p-4 space-y-1">
        {TOC_ITEMS.map((item, idx) => {
          const isActive = activeSection === item.id;
          return (
            <li key={item.id}>
              <button
                onClick={() => scrollTo(item.id)}
                className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-custom-pink/10 text-custom-pink font-semibold border-l-4 border-custom-pink pl-2"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <span className={`flex-shrink-0 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold mt-0.5 ${
                  isActive ? "bg-custom-pink text-white" : "bg-gray-200 text-gray-500"
                }`}>
                  {idx + 1}
                </span>
                <span className="leading-snug">{item.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const CiscoDetailPage = () => {
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const activeSection         = useActiveSection();

  useEffect(() => {
    cmsApi
      .getProgramBySlug("cisco-networking-academy")
      .then((res) => { if (res.success && res.program) setProgram(res.program); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const benefitsSource = program?.benefits ?? program?.careerOpportunities ?? null;
  const benefits       = benefitsSource ? parseBenefits(benefitsSource) : [];
  const admissionText  = program?.admissionRequirements ?? program?.requirements ?? "";

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page">
        <CourseHeader
          title="Cisco Networking"
          highlight="Academy"
          subtitle={program?.heroSubtitle || "World-class networking and cybersecurity education — brought to Tanzania through NetAcad."}
          backgroundImage={program?.heroImage ? getImageUrl(program.heroImage) : ciscoImg}
          cta={{ label: "Start Application", targetId: "application-form" }}
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Professional Courses", href: "/course/professional-courses" },
            { label: "Cisco Networking Academy" },
          ]}
        />

        {loading ? (
          <div className="flex justify-center items-center py-32">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-custom-pink" />
          </div>
        ) : (
          <div className="max-w-[1500px] mx-auto px-6">
            <div className="flex items-start gap-8">

              {/* Sticky Quick Navigation sidebar */}
              <aside className="hidden lg:block w-72 shrink-0 sticky top-20 self-start h-fit pt-10 pl-4 xl:pl-6">
                <QuickNavigation activeSection={activeSection} />
              </aside>

              {/* Main content column */}
              <div className="flex-1 min-w-0">

                {/* 1. Intro description */}
                <div id="intro">
                  <AnimatedSection title={program?.name || "Cisco Networking Academy"}>
                    <p className="text-base md:text-lg lg:text-xl text-gray-800 leading-loose">
                      {program?.description}
                    </p>
                  </AnimatedSection>
                </div>

                {/* 2. Explore Career Paths */}
                <div id="career-paths">
                  <CareerPathsGrid rawJson={program?.ciscoCertTracksSection} />
                </div>

                {/* 3. Why Study at JRIIT? */}
                <div id="why-study">
                  <ProgramBenefits programName="Cisco Networking Academy" benefits={benefits} />
                </div>

                {/* 4. Admission Requirements */}
                <div id="admission-requirements">
                  <AnimatedSection title="ADMISSION REQUIREMENTS">
                    <p className="text-base md:text-lg lg:text-xl text-gray-800 leading-loose tracking-wide">
                      {admissionText}
                    </p>
                  </AnimatedSection>
                </div>

                {/* 5. Application Form */}
                <div id="application-form">
                  <ProfessionalCourseApplicationForm
                    programName="Cisco Networking Academy"
                    fieldLabel="Professional Course"
                  />
                </div>

              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default CiscoDetailPage;
